import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { API } from '../constants/api.constants';

/**
 * Endpoint paths whose GET is a pure read, and which are therefore safe to collapse when two
 * callers request them concurrently.
 *
 * This is an ALLOWLIST by deliberate choice, not a denylist. Several GET endpoints in this API
 * are not reads at all: `/update-latest-counter` increments the product auto-ID counter and
 * `/users/resend-set-password-link-email` sends mail. Serving one response to two callers there
 * would hand two products the same number, or silently swallow an email. A denylist fails open
 * the day someone adds another such endpoint; this fails closed — an unlisted path simply keeps
 * today's behaviour.
 *
 * Only add a path here after confirming its GET handler performs no write, and that the client
 * actually reaches it with GET — `/masters/fetch-type-based-category` for instance is a POST, so
 * listing it here would imply a coverage this layer cannot provide.
 */
const DEDUPABLE_PATHS: readonly string[] = [
  API.SYSTEM_MASTER.GET_ALL_BRANDS,
  API.SYSTEM_MASTER.GET_ALL_SUB_BRAND,
  API.SYSTEM_MASTER.GET_ALL_SEASONS,
  API.SYSTEM_MASTER.GET_ALL_PC_TYPE,
  API.SYSTEM_MASTER.GET_ALL_PRODUCT_STATUS_MASTER,
  API.SYSTEM_MASTER.GET_ALL_LENGTHS,
  API.SYSTEM_MASTER.GET_ALL_LICENSOR,
  API.SYSTEM_MASTER.GET_ALL_LICENSE,
  API.SYSTEM_MASTER.GET_ALL_LIKENESS,
  API.SYSTEM_MASTER.GET_MULTI_CUSTOM_ATTRIBUTE,
  API.SYSTEM_MASTER.SAMPLE_TYPE_GROUP,
  API.SYSTEM_MASTER.AUTO_ID_GENERATOR,
  API.BOM_LIBRARY_FETCH_REFERENCE_SUPPLIER.GET,
];

/** Extra per-request options this layer understands, alongside the standard axios config. */
export interface DedupeRequestConfig extends AxiosRequestConfig {
  /** Set to false to force a fresh request even for an allowlisted path. */
  dedupe?: boolean;
}

/**
 * Serialise a value with object keys in a stable order, so that `{ a: 1, b: 2 }` and
 * `{ b: 2, a: 1 }` — which axios sends as the same query string — produce the same key.
 * Array order is preserved, because it is significant in a query string.
 * @param value the value to serialise
 * @returns a key-order-independent string form of the value
 */
const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'undefined';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const source = value as Record<string, unknown>;
  const body = Object.keys(source)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(source[key])}`)
    .join(',');
  return `{${body}}`;
};

/**
 * Drop any inline query string, so a URL that carries its params in the path and one that
 * carries them in the config are compared on the same footing.
 * @param url the request URL
 * @returns the URL up to the first `?`
 */
const stripQuery = (url: string): string => url.split('?')[0] ?? url;

/**
 * Build the identity of a GET request. Two requests share a key only when they would produce
 * byte-identical responses, so the key spans the params as well as the URL — categories 1 to 4
 * differ only by their params. (The category masters themselves go over POST, so they are not
 * listed above and never reach this layer.)
 * @param url the absolute request URL
 * @param config the axios config for the request
 * @returns the de-duplication key
 */
export const buildDedupeKey = (url: string, config?: DedupeRequestConfig): string =>
  `${stripQuery(url)}|${stableStringify(config?.params)}|${config?.responseType ?? 'json'}`;

/**
 * Whether a GET may be collapsed into an identical one already in flight.
 * @param url the absolute request URL
 * @param config the axios config for the request
 * @returns true when the request is an allowlisted, side-effect-free, plain-JSON read
 */
export const isDedupable = (url: string, config?: DedupeRequestConfig): boolean => {
  if (typeof url !== 'string' || !url) return false;
  if (config?.dedupe === false) return false;
  /* axios 0.21 cancels through cancelToken. Two callers sharing one request would let either
     one's cancellation abort the other's, so cancellable requests always stand alone. */
  if (config?.cancelToken) return false;
  /* Downloads: the payload is a Blob or ArrayBuffer that this layer will not copy safely. */
  if (config?.responseType && config.responseType !== 'json') return false;
  if (config?.onDownloadProgress || config?.onUploadProgress) return false;

  const path = stripQuery(url);
  return DEDUPABLE_PATHS.some((allowed) => Boolean(allowed) && path.endsWith(allowed));
};

/**
 * Copy a payload for a caller that joined a request already in flight.
 *
 * The originating caller keeps the original object, exactly as it would without this layer. Every
 * later caller gets its own copy, so one consumer mutating what it received cannot corrupt the
 * value another consumer already put in the store.
 * @param value the resolved response payload
 * @returns an independent copy, or the original when it cannot be copied
 */
const cloneForJoiner = <T>(value: T): T => {
  if (value === null || typeof value !== 'object') return value;
  const { structuredClone: clone } = globalThis as { structuredClone?: <V>(input: V) => V };
  try {
    return clone ? clone(value) : (JSON.parse(JSON.stringify(value)) as T);
  } catch (error) {
    /* Not copyable (never expected for a JSON response). Sharing the reference is still
       correct behaviour — it is only the mutation isolation that is lost. */
    return value;
  }
};

/**
 * Wrap an axios instance's `get` so that identical, allowlisted, concurrent reads issue one
 * request and fan the result out to every caller.
 *
 * This exists because a single mount of the product table view fires the same master reads from
 * two independent places — the Provider and the inline-options hook — and each pays a full round
 * trip. Collapsing them needs no change at any of the ~550 existing call sites.
 *
 * The entry is cleared the moment the shared request settles, so this only ever joins callers
 * that overlap in time; it is not a cache, and it never serves a completed response.
 * @param client the axios instance to instrument
 * @returns the same instance, with GET de-duplication installed
 */
export const installGetDedupe = (client: AxiosInstance): AxiosInstance => {
  const inFlight = new Map<string, Promise<unknown>>();
  const originalGet = client.get.bind(client);

  const dedupedGet = (url: string, config?: DedupeRequestConfig): Promise<unknown> => {
    if (!isDedupable(url, config)) return originalGet(url, config);

    const key = buildDedupeKey(url, config);
    const pending = inFlight.get(key);
    if (pending) return pending.then(cloneForJoiner);

    const shared = originalGet(url, config) as Promise<unknown>;
    /* Release the key before any caller's own handler runs, so a refetch triggered from a
       .then() on this request starts a fresh one rather than resolving against the old. */
    const release = () => {
      inFlight.delete(key);
    };
    shared.then(release, release);
    inFlight.set(key, shared);
    return shared;
  };

  Object.assign(client, { get: dedupedGet as AxiosInstance['get'] });
  return client;
};
